import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { CalendarDays, Clock, ArrowRight, ArrowLeft } from "lucide-react";
import { Reveal } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const strip = { background: "linear-gradient(90deg, #8c6d3f 0%, #e5c158 50%, #8c6d3f 100%)" };
const goldBg = { background: "linear-gradient(135deg, #a87f24 0%, #e5c158 50%, #d4af37 100%)" };

const SLOTS = ["9:00 AM", "10:30 AM", "12:00 PM", "1:30 PM", "3:00 PM", "4:30 PM"];

const to24h = (slot) => {
  const [hm, mer] = slot.split(" ");
  let [h, m] = hm.split(":").map(Number);
  if (mer === "PM" && h !== 12) h += 12;
  if (mer === "AM" && h === 12) h = 0;
  return { h, m };
};

const downloadIcs = (confirmed) => {
  const { h, m } = to24h(confirmed.time);
  const pad = (n) => String(n).padStart(2, "0");
  const start = `${confirmed.date.replace(/-/g, "")}T${pad(h)}${pad(m)}00`;
  const end = `${confirmed.date.replace(/-/g, "")}T${pad(h)}${pad(m + 20)}00`;
  const stamp = new Date().toISOString().replace(/[-:]/g, "").split(".")[0] + "Z";
  const ics = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//Emison Management//Funding Strategy Call//EN",
    "BEGIN:VEVENT",
    `UID:${confirmed.reference}@emisonmanagement.com`,
    `DTSTAMP:${stamp}`,
    `DTSTART;TZID=America/Chicago:${start}`,
    `DTEND;TZID=America/Chicago:${end}`,
    "SUMMARY:Emison Management — Funding Strategy Call",
    `DESCRIPTION:Your complimentary 20-minute funding strategy call. Reference: ${confirmed.reference}`,
    "LOCATION:Phone — Emison Management will call you",
    "END:VEVENT",
    "END:VCALENDAR",
  ].join("\r\n");
  const url = URL.createObjectURL(new Blob([ics], { type: "text/calendar" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = `emison-strategy-call-${confirmed.reference}.ics`;
  a.click();
  URL.revokeObjectURL(url);
};

const Booking = () => {
  const [params] = useSearchParams();
  const [name, setName] = useState(params.get("name") || "");
  const [email, setEmail] = useState(params.get("email") || "");
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [confirmed, setConfirmed] = useState(null);
  const appRef = params.get("ref") || "";

  const minDate = new Date(Date.now() + 86400000).toISOString().split("T")[0];

  const submit = async (e) => {
    e.preventDefault();
    if (!date || !time) {
      toast.error("Choose a date and time for your call.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/bookings`, {
        name, email, reference: appRef || null, date, time, note: note || null,
      });
      setConfirmed({ ...res.data, date, time });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main data-testid="booking-page" className="pt-20 relative overflow-hidden">
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0"
        style={{ background: "radial-gradient(900px 420px at 50% 8%, rgba(212,175,55,0.12), transparent 65%)" }}
      />
      <span
        aria-hidden="true"
        className="pointer-events-none select-none absolute top-24 left-1/2 -translate-x-1/2 font-serif italic whitespace-nowrap text-[20vw] leading-none text-transparent"
        style={{ WebkitTextStroke: "1px rgba(212,175,55,0.10)" }}
      >
        Encore
      </span>

      <section className="relative py-20 lg:py-28 px-6 lg:px-12 max-w-3xl mx-auto">
        <Reveal className="text-center">
          <div className="flex items-center justify-center gap-4 mb-8">
            <span className="h-px w-12 sm:w-20 bg-gradient-to-r from-transparent to-[#d4af37]" />
            <span className="w-2 h-2 rotate-45 bg-[#d4af37]" />
            <p className="text-[10px] font-mono-accent uppercase tracking-[0.4em] text-gold">
              Complimentary · 20 Minutes
            </p>
            <span className="w-2 h-2 rotate-45 bg-[#d4af37]" />
            <span className="h-px w-12 sm:w-20 bg-gradient-to-l from-transparent to-[#d4af37]" />
          </div>
          <span
            className="mx-auto w-12 h-12 rotate-45 flex items-center justify-center mb-8 shadow-[0_8px_20px_rgba(201,162,39,0.4)]"
            style={goldBg}
          >
            <CalendarDays className="-rotate-45 w-5 h-5 text-white" />
          </span>
          <h1 className="font-serif font-semibold text-4xl sm:text-5xl lg:text-6xl tracking-tight text-[#f3e5c8] leading-[1.08]">
            Your free <span className="italic gold-text">funding strategy call.</span>
          </h1>
          <p className="mt-6 text-base text-[#cbb493] leading-relaxed max-w-xl mx-auto">
            A senior funding consultant walks you through your options, answers
            every question, and maps the fastest route to your capital. No
            obligation — just clarity.
          </p>
        </Reveal>

        {confirmed ? (
          <Reveal className="mt-14">
            <div
              data-testid="booking-confirmed"
              className="relative bg-[#fffdf9] border border-[#e3d3ac] p-10 lg:p-14 text-center shadow-[0_30px_80px_rgba(45,26,11,0.22)]"
            >
              <span className="absolute bottom-0 left-0 right-0 h-[3px]" style={strip} />
              <span
                className="mx-auto w-12 h-12 rotate-45 flex items-center justify-center mb-8 shadow-[0_8px_20px_rgba(201,162,39,0.4)]"
                style={goldBg}
              >
                <Clock className="-rotate-45 w-5 h-5 text-white" />
              </span>
              <h2 className="font-serif font-semibold text-3xl text-[#4D4232]">
                Your call is <span className="italic gold-text">on the books.</span>
              </h2>
              <p className="mt-4 text-sm text-sand max-w-md mx-auto leading-relaxed">
                We&rsquo;re looking forward to it. A confirmation is on its way to your inbox.
              </p>
              <div className="mt-8 inline-block border border-[#e3d3ac] bg-white px-8 py-6">
                <p className="text-[9px] font-mono-accent uppercase tracking-[0.3em] text-stone">Booking Reference</p>
                <p data-testid="booking-reference" className="mt-2 font-mono-accent text-xl tracking-[0.2em] text-gold">
                  {confirmed.reference}
                </p>
                <p className="mt-3 font-serif text-lg text-[#4D4232]">
                  {new Date(`${confirmed.date}T12:00:00`).toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })} · {confirmed.time} CT
                </p>
              </div>
              <div className="mt-10 flex flex-wrap justify-center gap-4">
                <button
                  onClick={() => downloadIcs(confirmed)}
                  data-testid="booking-add-to-calendar"
                  className="sheen-btn inline-flex items-center gap-3 text-[#4D4232] text-[11px] font-semibold uppercase tracking-[0.25em] px-8 py-4"
                >
                  <CalendarDays className="w-4 h-4" /> Add To Calendar
                </button>
                <Link
                  to="/"
                  data-testid="booking-home-link"
                  className="btn-outline-lux inline-flex items-center gap-3 text-champagne text-[11px] font-semibold uppercase tracking-[0.25em] px-8 py-4 hover:text-gold"
                >
                  Return Home
                </Link>
              </div>
            </div>
          </Reveal>
        ) : (
          <Reveal delay={0.1} className="mt-14">
            <form
              onSubmit={submit}
              className="relative bg-[#fffdf9] border border-[#e3d3ac] p-8 lg:p-12 shadow-[0_40px_100px_rgba(45,26,11,0.3),0_0_60px_rgba(212,175,55,0.12)]"
            >
              <span className="absolute top-0 left-0 right-0 h-[3px]" style={strip} />
              <span className="absolute bottom-0 left-0 right-0 h-[3px]" style={strip} />
              <div className="absolute inset-2.5 border border-[#c9a227]/25 pointer-events-none" />
              {appRef && (
                <p className="mb-8 text-center text-[10px] font-mono-accent uppercase tracking-[0.3em] text-gold">
                  Linked to application <span className="text-[#4D4232]">{appRef}</span>
                </p>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-8">
                <div>
                  <label className="block text-[10px] font-mono-accent uppercase tracking-[0.25em] text-stone mb-2">Your Name</label>
                  <input
                    type="text" required value={name} onChange={(e) => setName(e.target.value)}
                    data-testid="booking-input-name"
                    className="w-full bg-[#faf7ef] border border-[#c9a227]/30 px-4 py-3.5 text-sm text-cream focus:outline-none focus:border-[#c9a227] transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-mono-accent uppercase tracking-[0.25em] text-stone mb-2">Email</label>
                  <input
                    type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                    data-testid="booking-input-email"
                    className="w-full bg-[#faf7ef] border border-[#c9a227]/30 px-4 py-3.5 text-sm text-cream focus:outline-none focus:border-[#c9a227] transition-colors"
                  />
                </div>
              </div>
              <div className="mb-8">
                <label className="block text-[10px] font-mono-accent uppercase tracking-[0.25em] text-stone mb-2">Choose Your Day</label>
                <input
                  type="date" required min={minDate} value={date} onChange={(e) => setDate(e.target.value)}
                  data-testid="booking-input-date"
                  className="w-full bg-[#faf7ef] border border-[#c9a227]/30 px-4 py-3.5 text-sm text-cream focus:outline-none focus:border-[#c9a227] transition-colors"
                />
              </div>
              <div className="mb-8">
                <label className="block text-[10px] font-mono-accent uppercase tracking-[0.25em] text-stone mb-3">
                  Choose Your Time <span className="normal-case tracking-normal">(Central)</span>
                </label>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {SLOTS.map((s) => (
                    <button
                      key={s} type="button"
                      onClick={() => setTime(s)}
                      data-testid={`booking-slot-${s.replace(/[:\s]/g, "-").toLowerCase()}`}
                      className={`px-4 py-3.5 text-sm font-serif transition-all duration-300 border ${
                        time === s
                          ? "text-[#4D4232] font-semibold shadow-[0_8px_20px_rgba(201,162,39,0.4)] scale-[1.03]"
                          : "border-[#e3d3ac] bg-white text-sand hover:border-[#c9a227]"
                      }`}
                      style={time === s ? goldBg : {}}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
              <div className="mb-10">
                <label className="block text-[10px] font-mono-accent uppercase tracking-[0.25em] text-stone mb-2">
                  Anything We Should Prepare? <span className="normal-case tracking-normal">(optional)</span>
                </label>
                <textarea
                  rows={3} value={note} onChange={(e) => setNote(e.target.value)}
                  placeholder="e.g. I want to fund a 20-city tour this fall…"
                  data-testid="booking-input-note"
                  className="w-full bg-[#faf7ef] border border-[#c9a227]/30 px-4 py-3.5 text-sm text-cream placeholder:text-stone/60 focus:outline-none focus:border-[#c9a227] transition-colors resize-none"
                />
              </div>
              <button
                type="submit" disabled={submitting}
                data-testid="booking-submit-button"
                className="sheen-btn w-full inline-flex items-center justify-center gap-3 text-[#4D4232] text-sm font-semibold uppercase tracking-[0.25em] px-8 py-5 disabled:opacity-40"
              >
                {submitting ? "Reserving…" : "Reserve My Call"} <ArrowRight className="w-4 h-4" />
              </button>
              <p className="mt-5 text-center text-[10px] font-mono-accent uppercase tracking-[0.2em] text-stone">
                Free · 20 minutes · No obligation
              </p>
            </form>
          </Reveal>
        )}
      </section>
    </main>
  );
};

export default Booking;
